import { Marker } from 'features/mapping/components/Map';
import { LockedMapPinLocation, MapPinLocation } from 'features/mapping/icons';
import { TOP_MARKER_Z_INDEX } from 'features/mapping/mapping.constants';
import { LatLng } from 'shared/types';
import { useCallback, useMemo, useState } from 'react';
import { MarkerDragEvent } from 'shared/map-exports';
import {
  useLockedMapPin,
  useMapPinEnabled,
  useIsMapPinActive,
  useSetMapPin,
  useLatLngFromUrl,
} from 'features/mapping/mapPin';
import { CenterMap } from 'features/mapping/components/CenterMap';
import { COORDINATE_DISPLAY_FRACTION_DIGITS } from '../../constants';

// The URL rounds coordinates, so a dropped pin read back from the URL is
// slightly off from the raw drop point. Compare within that precision.
const COORDINATE_TOLERANCE = 10 ** -COORDINATE_DISPLAY_FRACTION_DIGITS;

const isSamePosition = (a: LatLng, b: LatLng): boolean =>
  Math.abs(a.lat - b.lat) < COORDINATE_TOLERANCE &&
  Math.abs(a.lng - b.lng) < COORDINATE_TOLERANCE;

export const MapCenterWithPinMarker = (): JSX.Element | null => {
  const { latitudeParam, longitudeParam, zoomLevelParam } = useLatLngFromUrl();
  const [draggedCoordinates, setDraggedCoordinates] = useState<LatLng | null>(
    null,
  );
  const { setSelectedMapPin } = useSetMapPin();
  const { isLocked, lockedPin, goToLockedPin } = useLockedMapPin();
  const showMapPin = useMapPinEnabled();
  const showUrlMapPin = useIsMapPinActive();

  // It's memoized because is used inside 'CenterMap' component useEffect
  const coordinates: LatLng | null = useMemo(() => {
    if (!latitudeParam || !longitudeParam) {
      return null;
    }

    return { lat: latitudeParam, lng: longitudeParam };
  }, [latitudeParam, longitudeParam]);

  const handleDrag = useCallback(
    (e: MarkerDragEvent): void => {
      setDraggedCoordinates({ lat: e.lngLat.lat, lng: e.lngLat.lng });
      setSelectedMapPin(e.lngLat);
    },
    [setSelectedMapPin],
  );

  // True when the URL pin is the spot the user last dropped a dragged pin, so
  // the map doesn't jump under it. This compares the drop point to the URL
  // pin, not how far the pin was dragged. Any other pin (e.g. picked from
  // search) centers as usual.
  const isDraggedPin = Boolean(
    coordinates &&
      draggedCoordinates &&
      isSamePosition(coordinates, draggedCoordinates),
  );

  if (!coordinates && !lockedPin) {
    return null;
  }

  return (
    <>
      {showMapPin && isLocked && lockedPin && (
        <Marker
          icon={LockedMapPinLocation}
          position={lockedPin}
          onClick={(e) => {
            // Prevent interaction with other layers
            e.originalEvent.stopPropagation();
            goToLockedPin();
          }}
          scale={1.5}
          anchor="center"
        />
      )}
      {showUrlMapPin && !isLocked && coordinates && (
        <Marker
          icon={MapPinLocation}
          position={coordinates}
          draggable
          // Wait for drag to end before setting the map drawer entity. Downstream, this avoids unnecessary metadata fetches.
          onDragEnd={handleDrag}
          scale={1.5}
          anchor="center"
          zIndex={TOP_MARKER_Z_INDEX}
        />
      )}
      <CenterMap
        latLng={isDraggedPin ? null : coordinates}
        drawerIsOpen={showUrlMapPin}
        defaultSnapPointPct={0.47}
        zoom={zoomLevelParam}
      />
    </>
  );
};
